/**
 * useScrollCollapse
 *
 * Binary collapse toggle driven by scroll direction.
 * Sets a MotionValue to 0 (expanded) or 1 (collapsed) — no React state,
 * no re-renders in the host component. Consumers use useMotionValueEvent
 * to read changes where they need them.
 *
 *   1 → on first downward scroll
 *   0 → after sustained upward scroll (EXPAND_ACCUMULATION_PX)
 */

import { useMotionValue, type MotionValue } from 'framer-motion';
import { useCallback, useRef } from 'react';
import {
  applyScrollCollapseProgress,
  SCROLL_COLLAPSE_PROGRESS,
} from './scroll-collapse-state';

const EXPAND_ACCUMULATION_PX = 30;

interface ScrollCollapseController {
  value: MotionValue<number>;
  reportScrollTop: (scrollTop: number) => void;
  reseed: () => void;
  reset: () => void;
}

export function useScrollCollapse(): ScrollCollapseController {
  const value = useMotionValue<number>(SCROLL_COLLAPSE_PROGRESS.Expanded);
  const prevScrollTopRef = useRef<number | null>(null);
  const upwardAccumRef = useRef(0);
  const currentRef = useRef(SCROLL_COLLAPSE_PROGRESS.Expanded);

  const reportScrollTop = useCallback(
    (scrollTop: number) => {
      const prev = prevScrollTopRef.current;
      prevScrollTopRef.current = scrollTop;
      if (prev === null) return;

      const delta = scrollTop - prev;
      applyScrollCollapseProgress({
        delta,
        value,
        upwardAccumRef,
        currentRef,
        expandAccumulationPx: EXPAND_ACCUMULATION_PX,
      });
    },
    [value],
  );

  /** Reset scroll tracking without changing the collapsed state (e.g. on scroll container switch). */
  const reseed = useCallback(() => {
    prevScrollTopRef.current = null;
    upwardAccumRef.current = 0;
  }, []);

  /** Fully reset to expanded. */
  const reset = useCallback(() => {
    reseed();
    currentRef.current = SCROLL_COLLAPSE_PROGRESS.Expanded;
    value.set(SCROLL_COLLAPSE_PROGRESS.Expanded);
  }, [value, reseed]);

  return { value, reportScrollTop, reseed, reset };
}

export const useScrollCollapseProgress = useScrollCollapse;
